import React, { useState, useEffect } from 'react';
import { ShoppingBag, ArrowRight, ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { shopifyAPI } from '../../services/api';
import { featuredProducts as fallbackProducts } from '../../data/mock';

const FeaturedCollections = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await shopifyAPI.getProducts(8);
        if (data.products && data.products.length > 0) {
          setProducts(data.products);
        } else {
          // Fallback to mock data if no products
          setProducts(fallbackProducts.map(p => ({
            ...p,
            title: p.name,
            images: [p.image],
            productType: p.category,
            url: '#'
          })));
        }
      } catch (error) {
        console.error('Error fetching Shopify products:', error);
        // Fallback to mock data
        setProducts(fallbackProducts.map(p => ({
          ...p,
          title: p.name,
          images: [p.image],
          productType: p.category,
          url: '#'
        })));
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleShopNow = (url) => {
    window.open(url, '_blank');
  };

  return (
    <section id="shop" className="section-padding bg-[#0a0b0c]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#00d4aa] text-sm font-bold uppercase tracking-widest mb-4">
            Featured Gear
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white mb-4">
            Shop Collections
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            Premium performance gear designed for competitive players who demand the best.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-white/10 rounded-lg mb-4" />
                <div className="h-4 bg-white/10 rounded w-1/3 mb-2" />
                <div className="h-5 bg-white/10 rounded w-2/3 mb-2" />
                <div className="h-5 bg-white/10 rounded w-1/4" />
              </div>
            ))}
          </div>
        )}

        {/* Product Grid */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <div
                key={product.id || index}
                className="group card-metallic overflow-hidden cursor-pointer"
                onClick={() => handleShopNow(product.url)}
              >
                {/* Image Container */}
                <div className="relative aspect-square overflow-hidden bg-[#1a1c1f]">
                  <img
                    src={product.images?.[0] || product.image}
                    alt={product.title || product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  
                  {/* Badge for first product */}
                  {index === 0 && (
                    <Badge className="absolute top-3 left-3 bg-[#00d4aa] text-[#0a0b0c] font-bold uppercase text-xs">
                      New
                    </Badge>
                  )}
                  
                  {/* Popular badge for products with high price */}
                  {product.price > 40 && index !== 0 && (
                    <Badge className="absolute top-3 left-3 bg-[#00d4aa] text-[#0a0b0c] font-bold uppercase text-xs">
                      Popular
                    </Badge>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0b0c] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Quick shop button */}
                  <Button
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 bg-[#00d4aa] text-[#0a0b0c] hover:bg-[#33e0bb] rounded-full px-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShopNow(product.url);
                    }}
                  >
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    Shop Now
                  </Button>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <span className="text-[#00d4aa] text-xs font-medium uppercase tracking-wider">
                    {product.productType || product.category || 'Gear'}
                  </span>
                  <h3 className="text-white font-semibold mt-1 group-hover:text-[#00d4aa] transition-colors line-clamp-2">
                    {product.title || product.name}
                  </h3>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-white/80 font-bold">
                      ${(product.price || 0).toFixed(2)}
                    </p>
                    <ExternalLink className="h-4 w-4 text-white/40 group-hover:text-[#00d4aa] transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 group"
            onClick={() => window.open('https://gwins-gear.myshopify.com', '_blank')}
          >
            View All Products
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCollections;
